import type {
  ServiceAccountingFilters,
  ServiceOpType,
  WalletKind,
} from '../model/types';
import { utcToZonedTime } from 'date-fns-tz';
import { DISPLAY_TIME_ZONE } from '@shared/lib/format-date';
import { SERVICE_OPERATION_TYPES, SERVICE_WALLET_KINDS } from './constants';

export const DEFAULT_FILTERS: ServiceAccountingFilters = {
  search: '',
  dateFrom: '',
  dateTo: '',
  sort: 'date_desc',
  types: [],
  wallets: [],
  period: null,
};

export const toggleType = (
  types: ServiceOpType[],
  type: ServiceOpType,
): ServiceOpType[] =>
  types.includes(type) ? types.filter((item) => item !== type) : [...types, type];

export const toggleWallet = (wallets: WalletKind[], wallet: WalletKind): WalletKind[] =>
  wallets.includes(wallet)
    ? wallets.filter((item) => item !== wallet)
    : [...wallets, wallet];

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getPeriodDates = (
  period: 'today' | '7d' | '30d' | 'all',
): { dateFrom: string; dateTo: string } => {
  if (period === 'all') {
    return { dateFrom: '', dateTo: '' };
  }

  // Календарный день считаем в UTC+3, а не в часовом поясе браузера: иначе ночью по МСК период сдвигается на день.
  const to = utcToZonedTime(new Date(), DISPLAY_TIME_ZONE);
  const from = utcToZonedTime(new Date(), DISPLAY_TIME_ZONE);

  if (period === '7d') {
    from.setDate(from.getDate() - 6);
  } else if (period === '30d') {
    from.setDate(from.getDate() - 29);
  }

  return { dateFrom: formatLocalDate(from), dateTo: formatLocalDate(to) };
};

const isTypesNarrowed = (types: ServiceOpType[]): boolean =>
  types.length > 0 &&
  (types.length !== SERVICE_OPERATION_TYPES.length ||
    SERVICE_OPERATION_TYPES.some((type) => !types.includes(type)));

const isWalletsNarrowed = (wallets: WalletKind[]): boolean =>
  wallets.length > 0 &&
  (wallets.length !== SERVICE_WALLET_KINDS.length ||
    SERVICE_WALLET_KINDS.some((wallet) => !wallets.includes(wallet)));

/** Maps UI filters to GET /balances/accountings/service query params. */
export const toServiceAccountingsQuery = (
  filters: ServiceAccountingFilters,
): {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  types?: string;
  walletKinds?: string;
  sort?: ServiceAccountingFilters['sort'];
} => {
  const search = filters.search.trim();

  return {
    ...(search ? { search } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    ...(isTypesNarrowed(filters.types) ? { types: filters.types.join(',') } : {}),
    ...(isWalletsNarrowed(filters.wallets)
      ? { walletKinds: filters.wallets.join(',') }
      : {}),
    ...(filters.sort && filters.sort !== DEFAULT_FILTERS.sort ? { sort: filters.sort } : {}),
  };
};
